import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { axiosInstance } from '../../axios';
import { StockApiResponse, StockSearchResult } from './types';

export const STOCK_SEARCH_QUERY_KEY = ['stock-photos'] as const;

interface SearchParams {
  term: string;
  page?: number;
  orientation?: string;
  /** Zawęża wyniki do licencji freemium, którą da się pobrać na każdym planie. */
  freeOnly?: boolean;
}

/**
 * Wyszukiwanie w banku zdjęć. Nie zużywa puli pobrań, ale idzie przez nasz
 * backend, bo klucz Magnifica nie może opuścić serwera (pula jest wspólna dla
 * całej organizacji).
 *
 * Wyniki są trzymane w cache bezterminowo: ta sama fraza nie odpytuje API
 * drugi raz, co chroni limit 300 żądań na minutę na klucz.
 */
export const useSearchStockPhotos = (
  params: SearchParams,
  enabled: boolean,
): UseQueryResult<StockSearchResult, AxiosError> => useQuery<StockSearchResult, AxiosError>({
  queryKey: [
    ...STOCK_SEARCH_QUERY_KEY,
    params.term,
    params.page ?? 1,
    params.orientation ?? '',
    params.freeOnly ?? false,
  ],
  queryFn: async () => {
    const res = await axiosInstance.get<StockApiResponse<StockSearchResult>>('/v3-searchStockPhotos', {
      params: {
        term: params.term,
        page: params.page ?? 1,
        ...(params.orientation ? { orientation: params.orientation } : {}),
        ...(params.freeOnly ? { freeOnly: 'true' } : {}),
      },
    });
    return res.data.data;
  },
  enabled: enabled && params.term.trim().length > 1,
  staleTime: Infinity,
  refetchOnWindowFocus: false,
  refetchOnMount: false,
  retry: false,
});
